import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ChevronDown } from 'lucide-react';
import * as React from 'react';

import { cn } from '../../../lib';

type AccordionDisplay = 'default' | 'transparent' | 'inline';

const Accordion = AccordionPrimitive.Root;

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item> & { display?: AccordionDisplay }
>(({ className, display = 'default', ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn(
      display === 'default' && 'border-b',
      display === 'inline' && 'border rounded-md',
      className,
    )}
    {...props}
  />
));
AccordionItem.displayName = 'AccordionItem';

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> & { display?: AccordionDisplay }
>(({ className, children, display = 'default', ...props }, ref) => (
  <AccordionPrimitive.Header className="flex">
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        display === 'default' &&
          'flex flex-1 items-center justify-between py-4 font-medium transition-all hover:underline [&[data-state=open]>svg]:rotate-180',
        display === 'transparent' &&
          'flex flex-1 items-center gap-2 mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground transition-colors [&[data-state=closed]>svg]:-rotate-90',
        display === 'inline' &&
          'flex flex-1 items-center justify-between p-3 transition-colors hover:bg-muted/50 [&[data-state=open]>svg]:rotate-180',
        className,
      )}
      {...props}
    >
      <ChevronDown
        className={cn(
          'shrink-0 transition-transform duration-200',
          (display === 'default' || display === 'inline') && 'order-last h-4 w-4',
          display === 'transparent' && 'size-4',
        )}
      />
      {children}
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
));
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content> & { display?: AccordionDisplay }
>(({ className, children, display = 'default', ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className="overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
    {...props}
  >
    <div
      className={cn(
        display === 'default' && 'pb-4 pt-0',
        display === 'inline' && 'border-t px-3 pb-3 pt-0',
        className,
      )}
    >
      {children}
    </div>
  </AccordionPrimitive.Content>
));

AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
